import Backend from '../backend';

export type AuthPathClass =
  | 'refresh'
  | 'session_check_auth'
  | 'login'
  | 'logout'
  | 'protected_api'
  | 'optional_auth'
  | 'public_api'
  | 'unknown_api'
  | 'foreign';

export type AuthRouteInfo = {
  pathClass: AuthPathClass;
  canRefresh: boolean;
  canAttachRefreshHeader: boolean;
  canRecoverLostRotation: boolean;
  tombstoneExempt: boolean;
};

type Flags = Pick<
  AuthRouteInfo,
  'canRefresh' | 'canAttachRefreshHeader' | 'canRecoverLostRotation' | 'tombstoneExempt'
>;

const FLAGS: Record<AuthPathClass, Flags> = {
  protected_api: {
    canRefresh: true,
    canAttachRefreshHeader: true,
    canRecoverLostRotation: true,
    tombstoneExempt: false
  },
  session_check_auth: {
    canRefresh: true,
    canAttachRefreshHeader: true,
    canRecoverLostRotation: false,
    tombstoneExempt: false
  },
  optional_auth: {
    canRefresh: true,
    canAttachRefreshHeader: false,
    canRecoverLostRotation: false,
    tombstoneExempt: false
  },
  unknown_api: {
    canRefresh: true,
    canAttachRefreshHeader: false,
    canRecoverLostRotation: false,
    tombstoneExempt: false
  },
  public_api: {
    canRefresh: false,
    canAttachRefreshHeader: false,
    canRecoverLostRotation: false,
    tombstoneExempt: true
  },
  refresh: {
    canRefresh: false,
    canAttachRefreshHeader: false,
    canRecoverLostRotation: false,
    tombstoneExempt: false
  },
  login: {
    canRefresh: false,
    canAttachRefreshHeader: false,
    canRecoverLostRotation: false,
    tombstoneExempt: true
  },
  logout: {
    canRefresh: false,
    canAttachRefreshHeader: false,
    canRecoverLostRotation: false,
    tombstoneExempt: true
  },
  foreign: {
    canRefresh: false,
    canAttachRefreshHeader: false,
    canRecoverLostRotation: false,
    tombstoneExempt: true
  }
};

export const LOGIN_METHODS = [
  'loginUser',
  'wechatLogin',
  'wechatWebLogin',
  'appleLogin',
  'googleLogin',
  'requestOneTimeCode'
] as const;

export const LOGOUT_METHODS = ['logoutUser'] as const;
export const REFRESH_METHODS = ['refresh-token'] as const;
export const AUTH_METHODS = ['auth'] as const;

export const PUBLIC_METHODS = [
  'registerUser',
  'registerClient',
  'forgotPassword',
  'resetPassword',
  'verifyEmail',
  'check-email',
  'checkAdviserEmails',
  'trackAnonymous',
  'currencyExchangeRate',
  'insurers',
  'version',
  'events',
  'approveUser',
  'team-invitation',
  'addCampaignCode',
  'adviser-profile-plus',
  'configurations',
  'defaultMessages',
  'deleteRecord',
  'getAdvisorMessage',
  'getShareDocuments',
  'submitConsent',
  'verifyExportCode',
  'public-referrals',
  'public' // ClientQuiz / adviser notes (docs/specs/2026-09-24-demo-localstorage-to-db-design.md §17)
] as const;

/** decodeCookieOnly routes: refresh if a session exists, never attach x-refresh-token. */
export const OPTIONAL_AUTH_METHODS = [
  'acceptTeamInvitation',
  'cheque-wizards',
  'endpoints',
  'getImage',
  'pFin-calculator',
  'referrals',
  'submitReferral'
] as const;

/** decodeCookieOnly routes the server does not authorize from the session. */
export const OPTIONAL_AUTH_LOGOUT_SAFE = ['submitReferral'] as const;

/** Client-called authenticate routes that may carry x-refresh-token / recover a lost rotation. */
export const PROTECTED_METHODS = [
  'addMessage',
  'addPortfolio',
  'adminTeam',
  'adviser-export',
  'adviserBusinessAnalysis',
  'adviserCustomFilters',
  'adviserTemplate',
  'adviserTodo',
  'affiliates',
  'ai',
  'appointment',
  'assistant',
  'bedrockGenerateWithText',
  'changeInactiveUserEmail',
  'changePassword',
  'changeDemoClient',
  'checkClient',
  'checkQRCode',
  'checkTagField',
  'clearAllMsgOpt',
  'client', // GET client/:clientId/policies
  'client-quizzes', // ClientQuiz / adviser notes (docs/specs/2026-09-24-demo-localstorage-to-db-design.md §17)
  'clients',
  'connectedUserPolicies',
  'connection-requests',
  'createConfRoom',
  'createDefaultClient',
  'createNotification',
  'createQRCode',
  'deleteClientV2',
  'deletePolicy',
  'deleteShareFromList',
  'deleteShareList',
  'deleteTeamMember',
  'editPortfolio',
  'editPortfoliofield',
  'enableTracking',
  'emergency-exports',
  'factsheets', // ClientQuiz / adviser notes (docs/specs/2026-09-24-demo-localstorage-to-db-design.md §17)
  'feedback',
  'fetchFigureAdviser',
  'fetchMessage',
  'financial-pyramids',
  'figure',
  'functions',
  'generateExportCode',
  'getAdviserBlastNotification',
  'getAdviserBusiness',
  'getAdviserDashboard',
  'getAdviserTeamDetail',
  'getAnalysisData',
  'getBusinessSummary',
  'getClientCoveragePolicy',
  'getClientDetails',
  'getConnectedAdviser',
  'getConsent',
  'getDataAnalyticsToken',
  'getDuePolicy',
  'getFigureUser',
  'getMePortfolio',
  'getNewReferralCount',
  'getNotificationList',
  'getNotifications',
  'getPipeline',
  'getPortfolio',
  'getPortfolioJson',
  'getReferralRecords',
  'getReminderBadgeCount',
  'getShareFromData',
  'getShareMeDocuments',
  'getShareToData',
  'getTeamBlastNotification',
  'getTeamLeaderOf',
  'getTeamMemberPermission',
  'getTeamSetting',
  'getUploadDocuments',
  'getUser',
  'getUserTeamLogoNews',
  'illustration-ocr',
  'import',
  'insurerInfo',
  'markAllNotificationAsRead',
  'markNotificationAsRead',
  'media',
  'medical-card-coupon-codes',
  'medical-cards',
  'movePortfolio',
  'noticeAdviserToRegister',
  'openaiGenerateWithTextPolling',
  'openaiGenerateWithTextStreaming',
  'paidUpPolicies',
  'policyCount',
  'policyInfo',
  'policyList',
  'policy-reviews',
  'refreshNewReferrals',
  'sendTeamInvitation',
  'share',
  'shareDocuments',
  'shareWithAdviser',
  'speech',
  'statistics',
  'submitAdviserMessage',
  'submitClient',
  'submitPipeline',
  'teams',
  'thank-you',
  'track',
  'updateAdviserBlastNotification',
  'updateAdviserBusiness',
  'updateDB',
  'updateFactSheet',
  'updateFactsheetById',
  'updateFigureAdviser',
  'updateLanguage',
  'updateReminder',
  'updateShareDetails',
  'updateShareToList',
  'updateTagField',
  'updateTeamBlastNotification',
  'updateTeamSetting',
  'updateTeamsOfAdviser',
  'updateUserDetails',
  'uploadDocument',
  'uploadImage',
  'useQRCode',
  'user-data',
  'user-mission',
  'vertexCountTokens',
  'vertexGenerateWithTextStreaming',
  'wallet-pass'
] as const;

const loginSet = new Set<string>(LOGIN_METHODS);
const logoutSet = new Set<string>(LOGOUT_METHODS);
const refreshSet = new Set<string>(REFRESH_METHODS);
const authSet = new Set<string>(AUTH_METHODS);
const publicSet = new Set<string>(PUBLIC_METHODS);
const optionalSet = new Set<string>(OPTIONAL_AUTH_METHODS);
const optionalLogoutSafeSet = new Set<string>(OPTIONAL_AUTH_LOGOUT_SAFE);
const protectedSet = new Set<string>(PROTECTED_METHODS);

function backendOrigin(): string {
  try {
    return new URL(Backend.methodUrl('')).origin;
  } catch (_) {
    return '';
  }
}

export function parseRequestUrl(url: string): { origin: string; pathname: string } | null {
  if (!url || typeof url !== 'string') {
    return null;
  }
  try {
    const base =
      typeof window !== 'undefined' && window.location && window.location.origin
        ? window.location.origin
        : 'http://local.invalid';
    const parsed = url.includes('://') ? new URL(url) : new URL(url, base);
    let pathname = parsed.pathname || '';
    if (pathname.length > 1 && pathname.endsWith('/')) {
      pathname = pathname.slice(0, -1);
    }
    return { origin: parsed.origin, pathname };
  } catch (_) {
    return null;
  }
}

function methodName(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 'api' && parts[1] && /^v\d+$/.test(parts[1])) {
    return parts[2] || '';
  }
  return parts[0] || '';
}

export function classifyAuthRoute(url: string): AuthRouteInfo {
  const parsed = parseRequestUrl(url);
  if (!parsed) {
    return { pathClass: 'foreign', ...FLAGS.foreign };
  }

  const apiOrigin = backendOrigin();
  if (apiOrigin && parsed.origin !== apiOrigin) {
    return { pathClass: 'foreign', ...FLAGS.foreign };
  }

  const name = methodName(parsed.pathname);
  let pathClass: AuthPathClass = 'unknown_api';
  if (refreshSet.has(name) || parsed.pathname.endsWith('/refresh-token')) {
    pathClass = 'refresh';
  } else if (authSet.has(name) && (parsed.pathname.endsWith('/auth') || parsed.pathname.endsWith('/auth/'))) {
    pathClass = 'session_check_auth';
  } else if (loginSet.has(name)) {
    pathClass = 'login';
  } else if (logoutSet.has(name)) {
    pathClass = 'logout';
  } else if (publicSet.has(name)) {
    pathClass = 'public_api';
  } else if (optionalSet.has(name)) {
    pathClass = 'optional_auth';
  } else if (protectedSet.has(name)) {
    pathClass = 'protected_api';
  }

  const info = { pathClass, ...FLAGS[pathClass] };
  if (pathClass === 'optional_auth' && optionalLogoutSafeSet.has(name)) {
    info.tombstoneExempt = true;
  }
  return info;
}

export function telemetryPathClass(pathClass: AuthPathClass): string {
  if (pathClass === 'session_check_auth') return 'auth';
  if (pathClass === 'protected_api' || pathClass === 'optional_auth' || pathClass === 'unknown_api') return 'api';
  return pathClass;
}
